import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAdminScope } from '@/contexts/AdminScopeContext';
import {
  createFormForUser,
  deleteFormForUser,
  listFormsForUser,
} from '@/services/formsApi';
import { FORM_FILING_TYPE, type FormFilingType } from '@/constants/formConstants';
import type { FormListItem } from '@/services/formsApi';
import { getUserById } from '@/services/userApi';
import { getFullName } from '@/lib/utils';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { CopyFromPriorYearDialog } from '@/components/tax-form/CopyFromPriorYearDialog';
import { NewBusinessFormDialog } from '@/components/tax-form/NewBusinessFormDialog';
import { ArrowLeft, Briefcase, Trash2, User } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import type { UserListItem } from '@/types/user';

export default function AdminTaxFormLandingPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { selectedYear, selectedTenantId } = useAdminScope();
  const [user, setUser] = useState<UserListItem | null>(null);
  const [forms, setForms] = useState<FormListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteConfirmFormId, setDeleteConfirmFormId] = useState<string | null>(null);
  const [showNewBusinessDialog, setShowNewBusinessDialog] = useState(false);
  const [showCopyFromPriorDialog, setShowCopyFromPriorDialog] = useState(false);

  const loadUserAndForms = async () => {
    if (!userId) return;
    try {
      const [userData, list] = await Promise.all([
        getUserById(userId, selectedTenantId),
        listFormsForUser(userId, {
          taxYear: selectedYear,
          tenantId: selectedTenantId,
        }),
      ]);
      setUser(userData);
      setForms(list);
    } catch {
      toast.error('Failed to load user or tax forms');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUserAndForms();
  }, [userId, selectedYear, selectedTenantId]);

  const listPriorIndividualForms = useCallback(
    async (beforeTaxYear: number) => {
      if (!userId) return [];
      return listFormsForUser(userId, {
        beforeTaxYear,
        filingType: FORM_FILING_TYPE.INDIVIDUAL,
        tenantId: selectedTenantId,
      });
    },
    [userId, selectedTenantId]
  );

  const handleStartNew = async (filingType: FormFilingType) => {
    if (!userId) return;
    if (filingType === FORM_FILING_TYPE.BUSINESS) {
      setShowNewBusinessDialog(true);
      return;
    }
    setShowCopyFromPriorDialog(true);
  };

  const handleCreateIndividualForm = async (options: {
    mode: 'blank' | 'copy';
    copyPersonalInfoFromFormId?: string;
  }) => {
    if (!userId) return;
    setCreating(true);
    try {
      const created = await createFormForUser(
        userId,
        {
          filingType: FORM_FILING_TYPE.INDIVIDUAL,
          taxYear: selectedYear,
          ...(options.mode === 'copy' && options.copyPersonalInfoFromFormId
            ? { copyPersonalInfoFromFormId: options.copyPersonalInfoFromFormId }
            : {}),
        },
        { tenantId: selectedTenantId }
      );
      toast.success(
        options.mode === 'copy'
          ? 'Tax form created with prior-year personal info'
          : 'Tax form created'
      );
      setShowCopyFromPriorDialog(false);
      navigate(`/admin/users/${encodeURIComponent(userId)}/tax-form/${created.id}`);
    } catch {
      toast.error('Failed to create tax form');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateBusinessForm = async (formName: string) => {
    if (!userId) return;
    setCreating(true);
    try {
      const created = await createFormForUser(
        userId,
        {
          filingType: FORM_FILING_TYPE.BUSINESS,
          taxYear: selectedYear,
          formName: formName || undefined,
        },
        { tenantId: selectedTenantId }
      );
      toast.success('Tax form created');
      setShowNewBusinessDialog(false);
      navigate(`/admin/users/${encodeURIComponent(userId)}/tax-form/${created.id}`);
    } catch {
      toast.error('Failed to create tax form');
    } finally {
      setCreating(false);
    }
  };

  const handleConfirmDeleteBusinessForm = async () => {
    const formId = deleteConfirmFormId;
    if (!userId || !formId) return;
    setDeletingId(formId);
    setDeleteConfirmFormId(null);
    try {
      await deleteFormForUser(userId, formId, { tenantId: selectedTenantId });
      toast.success('Business form deleted');
      loadUserAndForms();
    } catch {
      toast.error('Failed to delete form');
    } finally {
      setDeletingId(null);
    }
  };

  const individualForm = !loading
    ? forms.find((f) => f.filingType === FORM_FILING_TYPE.INDIVIDUAL) ?? null
    : null;
  const businessForms = !loading
    ? forms.filter((f) => f.filingType === FORM_FILING_TYPE.BUSINESS)
    : [];

  if (!userId) {
    return (
      <div className="space-y-4">
        <p className="text-muted-foreground">Missing user.</p>
        <Button variant="outline" onClick={() => navigate('/admin/users')}>
          Back to users
        </Button>
      </div>
    );
  }

  if (loading && !user) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 bg-muted animate-pulse rounded" />
        <div className="grid gap-4 md:grid-cols-2">
          <div className="h-48 bg-muted animate-pulse rounded-lg" />
          <div className="h-48 bg-muted animate-pulse rounded-lg" />
        </div>
      </div>
    );
  }

  const clientName = user ? getFullName(user) : userId;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <Button
          variant="ghost"
          size="sm"
          className="w-fit -ml-2"
          onClick={() => navigate(`/admin/users/${encodeURIComponent(userId)}`)}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to {clientName}
        </Button>
        <h1 className="text-2xl font-bold tracking-tight">Tax forms — {clientName}</h1>
        <p className="text-muted-foreground">
          Fill or manage tax forms on behalf of this client. Year: {selectedYear}. Progress is saved per section.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="rounded-lg bg-primary/10 p-3">
                <User className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-semibold text-lg">Individual</h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Personal income tax (1040). Personal info, income, deductions, and more.
                </p>
                {individualForm ? (
                  <Button
                    className="mt-4"
                    variant="outline"
                    onClick={() =>
                      navigate(
                        `/admin/users/${encodeURIComponent(userId)}/tax-form/${individualForm.id}`
                      )
                    }
                  >
                    Continue editing
                  </Button>
                ) : (
                  <Button
                    className="mt-4"
                    onClick={() => handleStartNew(FORM_FILING_TYPE.INDIVIDUAL)}
                    disabled={creating}
                  >
                    {creating ? 'Creating…' : 'Start new Individual form'}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="rounded-lg bg-primary/10 p-3">
                <Briefcase className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-semibold text-lg">Business</h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Business tax (Schedule C / entity). Business details, income, expenses, vehicle.
                </p>
                <div className="mt-4 flex flex-col gap-2">
                  {businessForms.map((form, index) => (
                    <div key={form.id} className="flex items-center gap-2">
                      <span className="min-w-0 flex-1 truncate font-medium" title={form.formName?.trim() || `Business form ${index + 1}`}>
                        {form.formName?.trim() || `Business form ${index + 1}`}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          navigate(
                            `/admin/users/${encodeURIComponent(userId)}/tax-form/${form.id}`
                          )
                        }
                      >
                        Continue editing
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="shrink-0 text-destructive hover:text-destructive"
                        onClick={() => setDeleteConfirmFormId(form.id)}
                        disabled={deletingId === form.id}
                        title="Delete this business form"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    onClick={() => handleStartNew(FORM_FILING_TYPE.BUSINESS)}
                    disabled={creating}
                  >
                    {businessForms.length === 0 ? 'Start new Business form' : 'Add another Business form'}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <CopyFromPriorYearDialog
        open={showCopyFromPriorDialog}
        onOpenChange={setShowCopyFromPriorDialog}
        selectedYear={selectedYear}
        listPriorForms={listPriorIndividualForms}
        onCreate={handleCreateIndividualForm}
        loading={creating}
      />

      <NewBusinessFormDialog
        open={showNewBusinessDialog}
        onOpenChange={setShowNewBusinessDialog}
        defaultFormName={`Business form ${businessForms.length + 1}`}
        onCreate={handleCreateBusinessForm}
        loading={creating}
      />

      <ConfirmDialog
        open={deleteConfirmFormId !== null}
        onOpenChange={(open) => !open && setDeleteConfirmFormId(null)}
        title="Delete business form"
        description="Are you sure you want to delete this business form? This cannot be undone."
        confirmLabel="Delete"
        variant="destructive"
        loading={deletingId !== null}
        onConfirm={handleConfirmDeleteBusinessForm}
      />
    </div>
  );
}
